import { zodResolver } from "@hookform/resolvers/zod";
import { createId } from "@paralleldrive/cuid2";
import { ArrowLeftIcon, CheckIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import { defaultSkill, skillSchema, type Skill } from "@reactive-resume/schema";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { SmartCombobox, type SmartComboboxOption } from "../shared/smart-combobox";
import { LEVELS } from "./suggestions";

type Values = z.infer<typeof skillSchema>;
type Props = {
  item?: Skill;
  suggestions: SmartComboboxOption[];
  onCancel: () => void;
  onSave: (value: Skill) => void;
};

export const SkillForm = ({ item, suggestions, onCancel, onSave }: Props) => {
  const form = useForm<Values>({
    resolver: zodResolver(skillSchema),
    mode: "onChange",
    defaultValues: { ...defaultSkill, id: createId(), ...item } as Values,
  });
  const [keyword, setKeyword] = useState("");
  const keywords = form.watch("keywords") ?? [];
  useEffect(() => form.reset({ ...defaultSkill, id: createId(), ...item } as Values), [form, item]);
  const addKeyword = () => {
    const value = keyword.trim();
    if (value && !keywords.includes(value))
      form.setValue("keywords", [...keywords, value], { shouldDirty: true });
    setKeyword("");
  };

  return (
    <div className="rounded-2xl border border-indigo-100 bg-white p-3.5 shadow-[0_10px_30px_rgba(30,27,75,0.05)] sm:p-4">
      <header className="mb-4 flex items-start justify-between border-b border-slate-100 pb-3">
        <div>
          <h3 className="text-sm font-bold text-indigo-950">
            {item ? "Modifier la compétence" : "Nouvelle compétence"}
          </h3>
          <p className="mt-0.5 text-[10px] text-slate-500">
            Choisissez une suggestion ou saisissez librement.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex min-h-8 items-center gap-1 rounded-lg px-2 text-[10px] font-bold text-slate-500 hover:bg-slate-50"
        >
          <ArrowLeftIcon size={13} /> Liste
        </button>
      </header>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((values) => onSave(values as Skill))}
          className="grid gap-3.5 sm:grid-cols-2"
        >
          <FormField
            name="name"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[11px] font-bold text-indigo-950">Compétence</FormLabel>
                <FormControl>
                  <SmartCombobox
                    value={field.value ?? ""}
                    options={suggestions}
                    placeholder="Ex. Gestion de projet"
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <FormField
            name="description"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[11px] font-bold text-indigo-950">
                  Précision{" "}
                  <span className="text-[8px] font-medium text-slate-400">Facultatif</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Ex. 4 ans de pratique"
                    className="min-h-10 rounded-xl bg-white text-xs"
                  />
                </FormControl>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <FormField
            name="level"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel className="text-[11px] font-bold text-indigo-950">Niveau</FormLabel>
                <FormControl>
                  <div className="grid grid-cols-5 gap-1.5">
                    {LEVELS.map((level) => (
                      <button
                        key={level.value}
                        type="button"
                        onClick={() => field.onChange(level.value)}
                        className={cn(
                          "min-h-12 rounded-xl border px-1 text-[8px] font-bold transition-all sm:text-[9px]",
                          field.value === level.value
                            ? "border-emerald-400 bg-emerald-50 text-emerald-700 shadow-sm"
                            : "border-slate-200 text-slate-500 hover:border-indigo-200 hover:bg-indigo-50",
                        )}
                      >
                        {level.value}
                        <span className="mt-0.5 block font-medium">{level.label}</span>
                      </button>
                    ))}
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="keywords"
            control={form.control}
            render={() => (
              <FormItem className="sm:col-span-2">
                <FormLabel className="text-[11px] font-bold text-indigo-950">
                  Mots-clés associés{" "}
                  <span className="text-[8px] font-medium text-slate-400">Facultatif</span>
                </FormLabel>
                <div className="flex gap-2">
                  <Input
                    value={keyword}
                    placeholder="Ajoutez un mot-clé"
                    onChange={(event) => setKeyword(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addKeyword();
                      }
                    }}
                    className="min-h-10 rounded-xl bg-white text-xs"
                  />
                  <button
                    type="button"
                    onClick={addKeyword}
                    className="grid size-10 shrink-0 place-items-center rounded-xl border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  >
                    <PlusIcon size={15} weight="bold" />
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {keywords.map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() =>
                        form.setValue(
                          "keywords",
                          keywords.filter((item) => item !== value),
                        )
                      }
                      className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-[9px] font-semibold text-indigo-700"
                    >
                      {value}
                      <XIcon size={10} />
                    </button>
                  ))}
                </div>
              </FormItem>
            )}
          />
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-3 sm:col-span-2">
            <button
              type="button"
              onClick={onCancel}
              className="min-h-10 rounded-xl border border-slate-200 px-4 text-xs font-bold text-slate-600 hover:bg-slate-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-indigo-700 px-4 text-xs font-bold text-white hover:bg-indigo-800"
            >
              <CheckIcon size={14} weight="bold" />
              {item ? "Enregistrer" : "Ajouter"}
            </button>
          </div>
        </form>
      </Form>
    </div>
  );
};
